import React from 'react';
import {
  Compass, MonitorSmartphone, Palette, Route, MessageSquare, Search,
  BarChart3, ShieldCheck, ListChecks, Rocket, KeyRound, LifeBuoy,
  Check, Zap,
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  EVERY_PROJECT_INCLUDES, OPTIONAL_SERVICES, CARE_ITEMS,
  PROCESS_STEPS_PRICING, TURNAROUND_FACTORS, PRICING_FAQS,
} from '../data/pricingData';
import { trackEvent } from '../lib/track';
import SeoGeoGrowth from './SeoGeoGrowth';
import PaidMedia from './PaidMedia';

const ICONS = {
  Compass, MonitorSmartphone, Palette, Route, MessageSquare, Search,
  BarChart3, ShieldCheck, ListChecks, Rocket, KeyRound, LifeBuoy,
};

const scrollToEnquiry = () => {
  document.querySelector('#enquiry')?.scrollIntoView({ behavior: 'smooth' });
};

function Includes() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">WHAT EVERY PROJECT <em>INCLUDES.</em></h2>
        </div>
        <div className="pp-includes" data-testid="pricing-includes">
          {EVERY_PROJECT_INCLUDES.map((item) => {
            const Icon = ICONS[item.icon];
            return (
              <div className="pp-include" key={item.label}>
                <span className="pp-include-icon"><Icon size={19} /></span>
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function OptionalServices() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">BUILD THE COMPLETE <em>DIGITAL PRESENCE.</em></h2>
          <p className="pp-lead">Website projects can also be supported by the wider Boost! team.</p>
        </div>
        <div className="pp-chips" data-testid="pricing-optional">
          {OPTIONAL_SERVICES.map((s) => (
            <span className="pp-chip" key={s}>{s}</span>
          ))}
        </div>
        <p className="pp-small-note">Optional services are scoped and quoted separately.</p>
      </div>
    </section>
  );
}

function Care() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-care" data-testid="pricing-care">
          <div>
            <h2 className="pp-h2 pp-display">SUPPORT BEYOND <em>LAUNCH.</em></h2>
            <p className="pp-lead">
              Once your website is live, Boost! can continue to manage its technical health, content
              and performance.
            </p>
            <button
              className="pp-btn pp-btn-primary"
              onClick={() => { trackEvent('package_cta_click', { package: 'website_care' }); scrollToEnquiry(); }}
              data-testid="pricing-cta-care"
            >
              Ask about website care
            </button>
          </div>
          <ul className="pp-care-list">
            {CARE_ITEMS.map((item) => (
              <li key={item}><Check size={16} /> {item}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">A CLEAR, COLLABORATIVE <em>PROCESS.</em></h2>
        </div>
        <div className="pp-process" data-testid="pricing-process">
          {PROCESS_STEPS_PRICING.map((s) => (
            <div className="pp-step" key={s.step}>
              <span className="pp-step-num">{s.step}</span>
              <h4>{s.title}</h4>
              <p>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Turnaround() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-turnaround" data-testid="pricing-turnaround">
          <div>
            <h2 className="pp-h2 pp-display">HOW QUICKLY CAN A WEBSITE BE <em>COMPLETED?</em></h2>
            <p className="pp-lead">
              A standard five-page business website will typically be completed within seven to ten
              working days once all required content, access and approvals have been supplied.
            </p>
            <p className="pp-lead">
              Larger projects, ecommerce stores and websites requiring complex functionality will take longer.
            </p>
            <div className="pp-priority-note">
              <Zap size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              A priority turnaround may be available for suitable projects, subject to capacity and an
              additional fee.
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 14, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8A8AA8', margin: '0 0 10px' }}>
              Timelines are affected by
            </h4>
            <ul className="pp-factors">
              {TURNAROUND_FACTORS.map((f) => (
                <li key={f}><Check size={15} /> {f}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingFAQ() {
  return (
    <section className="pp-section pp-section-alt" id="pricing-faq">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">FREQUENTLY ASKED <em>QUESTIONS.</em></h2>
        </div>
        <div className="pp-faq-wrap">
          <Accordion type="single" collapsible data-testid="pricing-faq">
            {PRICING_FAQS.map((faq, i) => (
              <AccordionItem key={faq.q} value={`pfaq-${i}`} className="pp-faq-item" data-testid={`pricing-faq-item-${i}`}>
                <AccordionTrigger className="pp-faq-trigger">{faq.q}</AccordionTrigger>
                <AccordionContent className="pp-faq-content">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

export default function PricingSections() {
  return (
    <>
      <Includes />
      <OptionalServices />
      <SeoGeoGrowth />
      <PaidMedia />
      <Care />
      <Process />
      <Turnaround />
      <PricingFAQ />
    </>
  );
}
